import { differenceInCalendarDays, format, isValid, parse, startOfDay, subDays } from "date-fns";

import { gstDate } from "utils/gst";

import { BaseOption } from "entities/common";

export type ReportLevel = "portfolio" | "asset";

export type ExportFormat = "pdf" | "excel" | "csv";

export type ReportPeriod = "last_30_days" | "last_3_months" | "last_6_months" | "last_year" | "custom";

export type ReportSegment = {
  value: string;
  title: string;
  subtitle: string;
};

export type ReportSummaryInput = {
  name: string;
  level: ReportLevel;
  companyLabel?: string;
  portfolioLabel?: string;
  assetLabel?: string;
  period: ReportPeriod;
  startDate: string;
  endDate: string;
  format: ExportFormat;
};

export const REPORT_LEVEL_DEFAULT: ReportLevel = "asset";
export const REPORT_PERIOD_DEFAULT: ReportPeriod = "last_6_months";
export const REPORT_PERIOD_CUSTOM: ReportPeriod = "custom";
export const EXPORT_FORMAT_DEFAULT: ExportFormat = "pdf";

export const REPORT_NAME_MAX_LENGTH = 150;
export const MAX_PERIOD_DAYS = 730;

const VALUE_FORMAT = "yyyy-MM-dd";
const DISPLAY_FORMAT = "dd.MM.yyyy";
const EMPTY_SLOT = "-";

export const REPORT_LEVEL_OPTIONS: ReportSegment[] = [
  { value: "portfolio", title: "Portfolio", subtitle: "Selected portfolio" },
  { value: "asset", title: "Asset", subtitle: "Individual asset" },
];

export const EXPORT_FORMAT_OPTIONS: ReportSegment[] = [
  { value: "pdf", title: "PDF", subtitle: "For inspectors" },
  { value: "excel", title: "Excel", subtitle: "For inspectors" },
  { value: "csv", title: "CSV", subtitle: "For inspectors" },
];

export const REPORT_PERIOD_OPTIONS: BaseOption[] = [
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_3_months", label: "Last 3 months" },
  { value: "last_6_months", label: "Last 6 months" },
  { value: "last_year", label: "Last year" },
  { value: "custom", label: "Custom range" },
];

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  pdf: "PDF",
  excel: "Excel",
  csv: "CSV",
};

export const REPORT_LEVEL_LABELS: Record<ReportLevel, string> = {
  portfolio: "Portfolio",
  asset: "Asset",
};

export const REPORT_ERRORS = {
  CHOOSE_OPTION: "Please choose an option.",
  START_DATE_REQUIRED: "Please select the start date.",
  END_DATE_REQUIRED: "Please select the end date.",
  PERIOD_TOO_LONG: "The selected period exceeds the maximum allowed range of 2 years.",
};

export const parseDateValue = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const parsed = parse(value, VALUE_FORMAT, new Date());
  return isValid(parsed) ? parsed : undefined;
};

export const getMaxSelectableDate = (): Date => subDays(startOfDay(new Date()), 1);

export const isPeriodWithinLimit = (startValue: string, endValue: string): boolean => {
  const start = parseDateValue(startValue);
  const end = parseDateValue(endValue);
  if (!start || !end) return true;
  return differenceInCalendarDays(end, start) + 1 <= MAX_PERIOD_DAYS;
};

const toDisplayDate = (value: string): string => {
  const parsed = parseDateValue(value);
  return parsed ? format(parsed, DISPLAY_FORMAT) : "";
};

export const buildSummaryLines = (input: ReportSummaryInput, isAdmin: boolean): string[] => {
  const lines: string[] = [];

  const name = input.name.trim();
  if (name) lines.push(`Name: ${name}`);

  const slots: string[] = [];
  if (isAdmin) slots.push(input.companyLabel?.trim() || "");
  slots.push(input.portfolioLabel?.trim() || "");
  if (input.level === "asset") slots.push(input.assetLabel?.trim() || "");

  const levelLabel = REPORT_LEVEL_LABELS[input.level];
  lines.push(
    slots.some(Boolean)
      ? `Level: ${levelLabel} (${slots.map((slot) => slot || EMPTY_SLOT).join(", ")})`
      : `Level: ${levelLabel}`,
  );

  if (input.period === REPORT_PERIOD_CUSTOM) {
    const start = toDisplayDate(input.startDate);
    const end = toDisplayDate(input.endDate);
    if (start || end) lines.push(`Period: ${start || EMPTY_SLOT} to ${end || EMPTY_SLOT}`);
  } else {
    const periodLabel = REPORT_PERIOD_OPTIONS.find((option) => option.value === input.period)?.label;
    if (periodLabel) lines.push(`Period: ${periodLabel}`);
  }

  lines.push(`Format: ${EXPORT_FORMAT_LABELS[input.format]}`);

  return lines;
};

export type ReportStatus = "queued" | "generating" | "ready" | "failed";

export type ReportItem = {
  id: number;
  name: string;
  report_type: string;
  scope_type: ReportLevel;
  company_name: string;
  portfolio_name: string;
  asset_name: string | null;
  period_from: string;
  period_to: string;
  export_format: ExportFormat;
  status: ReportStatus;
  generated_at: string | null;
  is_new: boolean;
};

export type ReportListResponse = {
  items: ReportItem[];
  total: number;
  page: number;
  page_size: number;
  has_reports: boolean;
};

export type CreateReportPayload = {
  name: string;
  level: ReportLevel;
  company_id: number | null;
  portfolio_id: number;
  asset_id: number | null;
  period: ReportPeriod;
  start_date: string | null;
  end_date: string | null;
  export_format: ExportFormat;
};

export const REPORT_FILE_EXTENSIONS: Record<ExportFormat, string> = {
  pdf: "pdf",
  excel: "xlsx",
  csv: "csv",
};

export const REPORT_TOASTS = {
  GENERATION_STARTED: "The report generation has started. Please wait for the generation to finish.",
  GENERATION_START_FAILED: "Failed to start report generation. Please try again later.",
  GENERATION_COMPLETED: "Report generation completed successfully.",
  GENERATION_FAILED: "Report generation failed. Please try again.",
  DOWNLOAD_FAILED: "Failed to download report. Please try again later.",
  DELETED: "The report was successfully deleted.",
  DELETE_FAILED: "Failed to delete report. Please try again later.",
};

export const GENERATION_FAILED_TOOLTIP = "Report generation failed. Please try again.";

export const isInFlight = (status: ReportStatus): boolean => status === "queued" || status === "generating";

export const formatPeriod = (from: string, to: string): string => `${toDisplayDate(from)}-${toDisplayDate(to)}`;

export const formatGeneratedAt = (iso: string | null): string => (iso ? gstDate(iso) : "");

export const scopeLabel = (item: ReportItem): string =>
  item.scope_type === "asset" ? `Asset: ${item.asset_name ?? ""}` : `Portfolio: ${item.portfolio_name}`;

export type ReportListFilters = {
  search: string;
  companyId: string;
  portfolioId: string;
  assetId: string;
  exportFormat: string;
  page: number;
  pageSize: number;
};

export const clampPage = (page: number, total: number, pageSize: number): number =>
  Math.min(page, Math.max(1, Math.ceil(total / pageSize)));

export const buildReportListQuery = (filters: ReportListFilters): string => {
  const params = new URLSearchParams();
  params.set("page", String(filters.page));
  params.set("page_size", String(filters.pageSize));

  const search = filters.search.trim();
  if (search) params.set("search", search);
  if (filters.companyId) params.set("company_id", filters.companyId);
  if (filters.portfolioId) params.set("portfolio_id", filters.portfolioId);
  if (filters.assetId) params.set("asset_id", filters.assetId);
  if (filters.exportFormat) params.set("export_format", filters.exportFormat);

  return `?${params.toString()}`;
};

export const statusTransitions = (
  previous: ReportItem[],
  next: ReportItem[],
): { id: number; status: ReportStatus }[] => {
  const before = new Map(previous.map((row) => [row.id, row.status]));
  return next
    .filter((row) => {
      const old = before.get(row.id);
      return old !== undefined && isInFlight(old) && !isInFlight(row.status);
    })
    .map((row) => ({ id: row.id, status: row.status }));
};
